import { NavLink } from "react-router-dom";
import {
  HomeIcon,
  TaskListIcon,
  ProjectIcon,
  UsersIcon,
  LogoutIcon,
  ChevronLeftIcon,
  TaskTemplateIcon,
  OverviewIcon,
  PersonPlusIcon,
  FeedbackIcon,
} from "@/lib/icons";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { canAccessRoute } from "@/lib/routeAccess";
import { SchedulingChangesDot } from "@/components/common/SchedulingChangesDot";
import { useSchedulingChangesProjectIds } from "@/hooks/useSchedulingSync";
import { useMemo, useState } from "react";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Etusivu", icon: HomeIcon },
  { to: "/tasks", label: "Tehtävähallinta", icon: TaskListIcon },
  { to: "/overview", label: "Tilannekatsaus", icon: OverviewIcon },
  { to: "/projects", label: "Hankkeet", icon: ProjectIcon },
  { to: "/substitutes", label: "Tuuraajat", icon: PersonPlusIcon },
  { to: "/task-templates", label: "Tehtäväpohjat", icon: TaskTemplateIcon },
  { to: "/users", label: "Käyttäjähallinta", icon: UsersIcon },
  { to: "/feedback", label: "Kehitys", icon: FeedbackIcon },
];

export function Sidebar() {
  const { user, dbUser, logout, isTeams } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { projectIds: schedulingChangeProjectIds } =
    useSchedulingChangesProjectIds();
  const schedulingChangesCount = schedulingChangeProjectIds.length;

  // Show only the pages this user may actually open. The floors come from the
  // same ROUTE_ACCESS map the route guards use, so a visible item always leads
  // somewhere the user is allowed to go.
  const navItems = useMemo(
    () => NAV_ITEMS.filter((item) => canAccessRoute(dbUser?.accessLevel, item.to)),
    [dbUser?.accessLevel],
  );

  const initials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() ?? "K";

  return (
    <aside
      className={cn(
        "flex h-dvh flex-col border-r bg-sidebar-primary py-3 shadow-lg transition-[width] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
        "duration-[1200ms]",
        isCollapsed ? "w-18" : "w-64",
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "flex h-16 items-center px-3 border-b border-sidebar-border bg-sidebar-primary",
          isCollapsed ? "justify-center" : "justify-between",
        )}
      >
        <img
          src="/Tencon_logo.png"
          alt="Tencon"
          className={cn(
            "h-full object-contain transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
            isCollapsed
              ? "w-0 opacity-0 scale-90"
              : "w-auto opacity-100 scale-100",
          )}
        />

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="text-neutral-200 hover:text-white hover:bg-neutral-800 shrink-0 hover:scale-110 active:scale-95 transition-[transform,color,background-color] duration-200"
        >
          {/* Single chevron that rotates 180° */}
          <ChevronLeftIcon
            className={cn(
              "h-5 w-5 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
              isCollapsed && "rotate-180",
            )}
          />
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-2 p-5">
        {navItems.map((item, index) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-md transition-colors duration-200",
                isCollapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
                isActive
                  ? "bg-sidebar-accent text-black"
                  : "text-white hover:bg-sidebar-accent hover:text-black",
              )
            }
          >
            <span className="relative flex shrink-0">
              <item.icon className="h-4 w-4 shrink-0" />
              {item.to === "/projects" && schedulingChangesCount > 0 && (
                <SchedulingChangesDot
                  className="absolute -right-1.5 -top-1"
                  title={`Sitedrive-muutoksia saatavilla ${schedulingChangesCount} hankkeessa`}
                />
              )}
            </span>
            <span
              className={cn(
                "whitespace-nowrap overflow-hidden transition-[opacity,transform] ease-[cubic-bezier(0.22,1,0.36,1)]",
                isCollapsed
                  ? "w-0 opacity-0 translate-x-[-8px] duration-300"
                  : "w-auto opacity-100 translate-x-0 duration-700",
              )}
              style={{
                transitionDelay: isCollapsed ? "0ms" : `${index * 50}ms`,
              }}
            >
              {item.label}
            </span>
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      {user && (
        <div className="border-t p-3">
          <div
            className={cn(
              "flex items-center",
              isCollapsed ? "justify-center" : "gap-3",
            )}
          >
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>

            <div
              className={cn(
                "min-w-0 flex-1 overflow-hidden transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                isCollapsed
                  ? "w-0 opacity-0 translate-x-[-8px]"
                  : "w-auto opacity-100 translate-x-0",
              )}
            >
              <p className="truncate text-sm text-neutral-200">
                {user.name}
              </p>
              <p className="truncate text-xs text-neutral-400">
                {user.email}
              </p>
            </div>

            {!isTeams && (
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className={cn(
                  "text-neutral-400 hover:text-white shrink-0 overflow-hidden transition-[opacity,width] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                  isCollapsed ? "w-0 opacity-0" : "opacity-100",
                )}
                tabIndex={isCollapsed ? -1 : undefined}
              >
                <LogoutIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}
